import { type CHCameraId, type DeviceEntity } from '@solinkcorp/api-types';
import { toastUnexpectedError } from '@solinkcorp/solink-components';
import { useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import { useCallback } from 'react';
import useDevices from 'src/hooks/devices/useDevices';
import useDevicesFromSite from 'src/hooks/devices/useDevicesFromSite';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import useIsUserStandard from 'src/hooks/useIsUserStandard';
import useSWR, { type KeyedMutator } from 'swr';
import { isProMonitored, sortCamerasByAlarm } from 'utils/AlarmUtil';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type DeviceStatuses, getDeviceStatuses, getSiteFeatures } from 'utils/LocationUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { DEFAULT_MAXIMUM_ALARM_CAMERAS, DEVICE_STATUS, GLOBAL_CAMERA_TAGS } from 'vd-constants';

interface AlarmCameraCountStatus {
  maximum: number;
  current: number;
}
export interface AlarmCameraCount {
  indoor: AlarmCameraCountStatus;
  outdoor: AlarmCameraCountStatus;
  grandfathered: number;
}

export interface UseAlarmCameras {
  alarmCamerasStatus: AlarmCamerasStatus;
  offlineCameras: CallhomeCamera[];
  onlineCameras: CallhomeCamera[];
  numberOfCameras: number;
  nonAlarmCameras?: CallhomeCamera[] | undefined;
  error: any;
  mutate: KeyedMutator<{
    alarmCameraIds: CHCameraId[];
    nonAlarmCameraIds: CHCameraId[];
    alarmCameras: CallhomeCamera[];
    nonAlarmCameras: CallhomeCamera[];
  }>;
  isValidating: boolean;
  isLoading: boolean;
  alarmCameraCount: AlarmCameraCount;
}
/*
 * Determines which cameras are alarm cameras based on whether they have motion zones setup
 * Also provides additional information about the alarm cameras (e.g. online/offline cameras, overall status, etc.)
 * @param location The location to get alarm cameras for
 * @param cameras The list of cameras
 * @param isVerifEye3 Whether the site is a VerifEye3 Group
 */
export function useAlarmCameras(
  location: VideoDiscoverySite | undefined,
  shouldFetch = true,
  isVerifEye3 = false,
) {
  const isStandardUser = useIsUserStandard();
  const { devicesById } = useDevices();
  const { deviceIds, devices } = useDevicesFromSite(location?.id ?? '');
  const localDevices = useDevicesAreLocal(deviceIds);

  const fetcher = useCallback(() => {
    return fetchSortedCameras(location?.cameras ?? [], localDevices, devicesById, isStandardUser);
  }, [location?.cameras, localDevices, devicesById, isStandardUser]);

  // Unique key for each location's alarm cameras
  const key =
    location?.id && shouldFetch ? `alarm-cameras-${location.id}-${location.lastUpdateTime}` : null;

  const config = useStaleSWRConfig(key);
  // This key is used to cache the data for each location
  const { data, ...rest } = useSWR(key, fetcher, config);

  const deviceStatuses = getDeviceStatuses({
    devices: devices || [],
    lastUpdateTime: location?.lastUpdateTime ?? new Date(0),
  });

  const alarmCameraCount = getAlarmCameraCount(location, devices, isVerifEye3);

  let indoorCount = 0;
  let outdoorCount = 0;

  data?.alarmCameras?.forEach((camera) => {
    if (camera.cameraTagIds?.includes(GLOBAL_CAMERA_TAGS.INDOOR)) {
      indoorCount++;
    }

    if (camera.cameraTagIds?.includes(GLOBAL_CAMERA_TAGS.OUTDOOR)) {
      outdoorCount++;
    }
  });

  alarmCameraCount.indoor.current = indoorCount;
  alarmCameraCount.outdoor.current = outdoorCount;

  return {
    ...data,
    ...rest,
    ...getAlarmCamerasInfo(deviceStatuses, data?.alarmCameras),
    alarmCameraCount,
  };
}

export type AlarmCamerasStatus = 'ONLINE' | 'CAMERAS_OFFLINE' | 'DEVICE_OFFLINE' | null;

async function fetchSortedCameras(
  cameras: CallhomeCamera[],
  localDevices: Record<string, boolean | undefined>,
  devicesById: Record<string, DeviceEntity>,
  isStandardUser: boolean,
) {
  try {
    return await sortCamerasByAlarm(cameras, localDevices, devicesById, isStandardUser);
  } catch (err: any) {
    toastUnexpectedError(err);
    throw err;
  }
}

function getAlarmCamerasInfo(
  deviceStatuses: DeviceStatuses,
  alarmCameras: CallhomeCamera[] | undefined,
): {
  alarmCamerasStatus: AlarmCamerasStatus;
  offlineCameras: CallhomeCamera[];
  onlineCameras: CallhomeCamera[];
  numberOfCameras: number;
} {
  if (!alarmCameras) {
    return { alarmCamerasStatus: null, onlineCameras: [], offlineCameras: [], numberOfCameras: 0 };
  }
  const numberOfCameras = alarmCameras?.length;

  const { onlineCameras, offlineCameras } = alarmCameras?.reduce(
    (acc, camera) => {
      const isOffline =
        camera.status !== 'online' || deviceStatuses[camera.deviceId] === DEVICE_STATUS.OFFLINE;

      if (isOffline) {
        acc.offlineCameras.push(camera);
      } else {
        acc.onlineCameras.push(camera);
      }

      return acc;
    },
    { onlineCameras: [], offlineCameras: [] } as {
      onlineCameras: CallhomeCamera[];
      offlineCameras: CallhomeCamera[];
    },
  ) ?? { onlineCameras: [], offlineCameras: [] };

  const areOfflineDevices = Object.values(deviceStatuses).some(
    (status) => status === DEVICE_STATUS.OFFLINE,
  );

  const numberOfOnlineCameras = onlineCameras.length;

  let alarmCamerasStatus: AlarmCamerasStatus = 'ONLINE';

  if (areOfflineDevices) {
    alarmCamerasStatus = 'DEVICE_OFFLINE';
  } else if (numberOfOnlineCameras < numberOfCameras) {
    alarmCamerasStatus = 'CAMERAS_OFFLINE';
  }
  return { alarmCamerasStatus, onlineCameras, offlineCameras, numberOfCameras };
}

function getAlarmCameraCount(
  location: VideoDiscoverySite | undefined,
  devices: DeviceEntity[],
  isVerifEye3: boolean,
): AlarmCameraCount {
  let maxIndoorCameras = DEFAULT_MAXIMUM_ALARM_CAMERAS;
  let maxOutdoorCameras = 0;
  let grandfathered = 0;

  const alarmCameraCount: AlarmCameraCount = {
    indoor: { current: 0, maximum: 0 },
    outdoor: { current: 0, maximum: 0 },
    grandfathered: 0,
  };

  const features = getSiteFeatures(devices) ?? {};

  if (location && isProMonitored(location.alarmConfig?.providerType, isVerifEye3)) {
    if (typeof features.alarms === 'object') {
      if ('maxOutdoorCameras' in features.alarms) {
        maxOutdoorCameras = features.alarms.maxOutdoorCameras as number;
      }

      if ('maxIndoorCameras' in features.alarms) {
        maxIndoorCameras = features.alarms.maxIndoorCameras as number;
      } else if ('maxCameras' in features.alarms) {
        maxIndoorCameras = features.alarms.maxCameras as number;
      }

      if ('grandfatheredCameras' in features.alarms) {
        grandfathered = features.alarms.grandfatheredCameras as number;
      }
    }

    // add number of video alarm tablet cameras to maxIndoorCameras
    maxIndoorCameras += location.cameras.filter((camera) =>
      camera.cameraTagIds.includes(GLOBAL_CAMERA_TAGS.VIDEO_ALARM_TABLET),
    ).length;

    alarmCameraCount.indoor.maximum = maxIndoorCameras;
    alarmCameraCount.outdoor.maximum = maxOutdoorCameras;
    alarmCameraCount.grandfathered = grandfathered;
  }

  return alarmCameraCount;
}
